import { DownloadIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckboxWithLabel } from "@/core/components/checkbox-with-label";
import { DetailField } from "@/core/components/detail-field";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import type { TimeRange } from "@/core/components/ui/time-range-picker";
import { Typography } from "@/core/components/ui/typography";
import { useBusiness } from "@/core/business";
import { formatMoney } from "@/core/utils/money";
import {
  useGetEnrichedSpiffApprovals,
  useBulkUpdateSpiffApprovals,
  filterApprovalsByTimeRange,
  generateCsvFilename,
  convertApprovalsToUserCsvData,
  generateCsvContent,
  SPIFF_APPROVAL_STATUSES,
  isEnrichedSpiffApprovalUser,
} from "@/spiff-approvals";

interface ExportSpiffsCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
  timeRange?: TimeRange | null;
}

export const ExportSpiffsCsvModal = ({
  isOpen,
  onClose,
  timeRange,
}: ExportSpiffsCsvModalProps) => {
  const { businessId, isSpiffOnly } = useBusiness();
  const { data: enrichedApprovals = [] } =
    useGetEnrichedSpiffApprovals(businessId);
  const { mutateAsync: bulkUpdateSpiffApprovalsAsync } =
    useBulkUpdateSpiffApprovals(businessId);
  const [markAsPaid, setMarkAsPaid] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const filteredApprovals = useMemo(
    () => filterApprovalsByTimeRange(enrichedApprovals, timeRange),
    [enrichedApprovals, timeRange],
  );

  const spiffApprovalIds = useMemo(
    () =>
      filteredApprovals.map((approval) =>
        // For single-user approvals, use the actual approval ID
        // For multi-user approvals, use the id directly
        isEnrichedSpiffApprovalUser(approval)
          ? approval.spiff_approval_id
          : approval.id,
      ),
    [filteredApprovals],
  );

  const alreadyPaidCount = useMemo(
    () =>
      filteredApprovals.filter(
        (approval) =>
          approval.status === SPIFF_APPROVAL_STATUSES.marked_as_paid,
      ).length,
    [filteredApprovals],
  );

  const totalAmount = useMemo(
    () =>
      filteredApprovals.reduce(
        (sum, approval) => sum + approval.total_amount,
        0,
      ),
    [filteredApprovals],
  );

  const csvData = useMemo(
    () => convertApprovalsToUserCsvData(filteredApprovals),
    [filteredApprovals],
  );

  const totalSpiffsCount = useMemo(
    () =>
      csvData.reduce(
        (total, user) =>
          total +
          user.signupCount +
          user.referralCreationCount +
          user.referralCompletionCount +
          user.fiveStarReviewCount,
        0,
      ),
    [csvData],
  );

  const handleExport = async () => {
    setIsExporting(true);

    try {
      // Prepare CSV export
      const csvContent = generateCsvContent(csvData, isSpiffOnly);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      // Prepare CSV download promise
      const downloadPromise = new Promise<void>((resolve, reject) => {
        try {
          const link = document.createElement("a");
          link.href = url;
          link.download = generateCsvFilename(timeRange);
          link.click();
          // Revoke on next tick to avoid revoking before download starts (Safari/Firefox quirk)
          setTimeout(() => URL.revokeObjectURL(url), 0);
          resolve();
        } catch (error) {
          reject(error);
        }
      });

      // Optional bulk update promise
      const updatePromise =
        markAsPaid && spiffApprovalIds.length > 0
          ? bulkUpdateSpiffApprovalsAsync({
              spiffApprovalIds,
              status: SPIFF_APPROVAL_STATUSES.marked_as_paid,
            })
          : Promise.resolve(null);

      // Execute in parallel
      const [updateResult] = await Promise.all([
        updatePromise,
        downloadPromise,
      ]);

      const updatedCount = updateResult?.updated_count ?? 0;
      const successMessage =
        markAsPaid && spiffApprovalIds.length > 0
          ? `CSV exported successfully and ${updatedCount} spiff approval${updatedCount === 1 ? "" : "s"} marked as paid`
          : "CSV exported successfully";

      toast.success(successMessage);
      onClose();
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to complete export operation");
    } finally {
      setIsExporting(false);
    }
  };

  const description = useMemo(
    () =>
      `You are about to export a CSV file with ${totalSpiffsCount} spiff${totalSpiffsCount === 1 ? "" : "s"} across ${csvData.length} user${csvData.length === 1 ? "" : "s"}.`,
    [totalSpiffsCount, csvData.length],
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent description={description}>
        <DialogHeader className="border-b">
          <DialogTitle>Export spiffs to CSV</DialogTitle>
        </DialogHeader>
        <Stack gap="md" className="px-6">
          {alreadyPaidCount > 0 && (
            <FeedbackBanner variant="warning">
              <Typography variant="bodyW2">
                {alreadyPaidCount} of the included spiff approvals{" "}
                {alreadyPaidCount === 1 ? "has" : "have"} already been marked as
                paid and will be included in this export.
              </Typography>
            </FeedbackBanner>
          )}

          <div className="grid w-full grid-cols-2 gap-4">
            <DetailField
              label="Approvals"
              message={filteredApprovals.length.toString()}
            />
            <DetailField label="Users" message={csvData.length.toString()} />
            <DetailField label="Spiffs" message={totalSpiffsCount.toString()} />
            <DetailField
              label="Total Amount"
              message={formatMoney(totalAmount)}
            />
          </div>

          <Typography>{description}</Typography>

          <CheckboxWithLabel
            id="mark-as-paid"
            label="Mark these spiffs as paid"
            checked={markAsPaid}
            onCheckedChange={(checked) => setMarkAsPaid(checked as boolean)}
          />
        </Stack>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            loading={isExporting}
            leftIcon={<DownloadIcon className="h-4 w-4" />}
          >
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
